import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, switchMap, tap } from 'rxjs';

import { Course } from '../models/course.model';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class CourseService {
  private apiUrl = environment.apiUrl + '/Course';

  http = inject(HttpClient);
  private coursesCache: Course[] = null;
  private coursesSubject = new BehaviorSubject<Course[]>(null);

  addCourse(course: Course): void {
    if (this.coursesCache) {
      this.coursesCache = [...this.coursesCache, course];
    } else {
      this.coursesCache = [course];
    }
  }

  createCourse(course: Course): Observable<Course> {
    return this.http.post<Course>(`${this.apiUrl}/CreateCourse`, course).pipe(
      tap((newCourse) => {
        newCourse.location = course.location;
        newCourse.courseType = course.courseType;
        this.addCourse(newCourse);
        this.coursesSubject.next(this.coursesCache);
      })
    );
  }

  getCourseObservable(): Observable<Course[]> {
    if (!this.coursesCache) {
      return this.getCourses().pipe(
        tap((courses) => {
          this.coursesCache = courses;
          this.coursesSubject.next(courses);
        }),
        switchMap(() => this.coursesSubject.asObservable())
      );
    } else {
      return this.coursesSubject.asObservable();
    }
  }

  deleteCourse(course: Course): Observable<any> {
    return this.http
      .delete(`${this.apiUrl}/DeleteCourse/${course.courseId}`)
      .pipe(
        tap(() => {
          this.coursesCache = this.coursesCache.filter(
            (c) => c.courseId !== course.courseId
          );
          this.coursesSubject.next(this.coursesCache);
        })
      );
  }

  getCourses(): Observable<Course[]> {
    return this.http.get<Course[]>(`${this.apiUrl}/GetAllCourses`);
  }

  getTimetableNameOptions(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/TimetableNameOptions`);
  }

  getCourseById(courseId: number): Observable<Course> {
  return this.http.get<Course>(
    `${this.apiUrl}/GetCourseById/${courseId}`
  );
}

  updateCourse(course: Course): Observable<Course> {
    return this.http
      .put<Course>(`${this.apiUrl}/UpdateCourse/${course.courseId}`, course)
      .pipe(
        tap((updatedCourse) => {
          updatedCourse.location = course.location;
          updatedCourse.courseType = course.courseType;
          const index = this.coursesCache.findIndex(
            (c) => c.courseId === updatedCourse.courseId
          );
          if (index !== -1) {
            this.coursesCache[index] = updatedCourse;
            this.coursesSubject.next(this.coursesCache);
          }
        })
      );
  }
  reloadCourses(): void {
    this.getCourses().subscribe({
      next: (courses) => {
        this.coursesCache = courses;
        this.coursesSubject.next(courses);
      },
      error: () => {
        console.error('Kurse konnten nicht neu geladen werden');
      },
    });
  }
  getParticipantsByCourseId(courseId: number) {
    return this.http.get<any[]>(
      `${environment.apiUrl}/Course/GetParticipantsByCourseId/${courseId}`
    );
  }
}
